import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorMessage } from '@/lib/apiError';
import { CinemaLoadError, PageControls, parseJsonObject } from '@/pages/CinemaShared';
import {
  useCreateActivityMutation,
  useDeleteActivityMutation,
  useGetActivitiesPageQuery,
  useGetActivityQuery,
  useGetActivityForAdminQuery,
  useUpdateActivityMutation,
  type Activity,
  type ActivityPageRow,
  type ActivityBody,
} from '@/store/api/ticketingApi';

const kinds: Activity['kind'][] = ['cinema', 'concert', 'theater', 'sports', 'class', 'workshop', 'reservation'];

export function ActivitiesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const createMode = id === 'new';
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<ActivityPageRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [editing, setEditing] = useState(createMode);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ kind: 'cinema' as Activity['kind'], name: '', status: 'active', config: '' });
  const page = useGetActivitiesPageQuery({ cursor, limit: 20 }, { skip: Boolean(id) });
  const detail = useGetActivityQuery(id ?? '', { skip: !id || createMode });
  const adminDetail = useGetActivityForAdminQuery(id ?? '', { skip: !id || createMode || !editing });
  const [createActivity, createState] = useCreateActivityMutation();
  const [updateActivity, updateState] = useUpdateActivityMutation();
  const [deleteActivity, deleteState] = useDeleteActivityMutation();

  useEffect(() => {
    if (!page.currentData) return;
    setRows((current) => cursor ? [...current, ...page.currentData!.data] : page.currentData!.data);
    setHasMore(page.currentData.pagination.hasMore);
  }, [page.currentData, cursor]);
  useEffect(() => {
    setCursor(undefined);
    setRows([]);
  }, [id]);
  useEffect(() => {
    if (adminDetail.data && editing && !createMode) {
      setForm({ kind: adminDetail.data.kind, name: adminDetail.data.name, status: adminDetail.data.status, config: JSON.stringify(adminDetail.data.config ?? {}, null, 2) });
    }
  }, [adminDetail.data, editing, createMode]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    let config: Record<string, unknown> | null;
    try {
      config = form.config.trim() ? parseJsonObject(form.config) : null;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Activity configuration must be a JSON object.');
      return;
    }
    const body: ActivityBody = { kind: form.kind, name: form.name.trim(), status: form.status.trim(), config };
    try {
      if (createMode) {
        const created = await createActivity(body).unwrap();
        navigate(`/cinema/activities/${encodeURIComponent(created.id)}`);
      } else if (id) {
        await updateActivity({ id, body }).unwrap();
        setEditing(false);
      }
    } catch (cause) {
      setError(apiErrorMessage(cause, 'Could not save this activity. Your inputs are retained; check them and try again.'));
    }
  }

  async function remove() {
    if (!id || createMode) return;
    setError('');
    try {
      await deleteActivity(id).unwrap();
      navigate('/cinema/activities');
    } catch (cause) {
      setError(apiErrorMessage(cause, 'Could not delete this activity. It may be referenced by a showtime; refresh and try again.'));
    }
  }

  if (!id) {
    return <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3"><div><h1 className="text-2xl font-bold">Cinema activities</h1><p className="text-sm text-muted-foreground">Organization-scoped activity catalog.</p></div><Button asChild><Link to="/cinema/activities/new">Create activity</Link></Button></header>
      {page.isLoading ? <LoadingRows label="Loading activities" columns={4} />
        : page.isError ? <CinemaLoadError label="activities" retry={() => void page.refetch()} />
          : rows.length === 0 ? <EmptyState title="No activities yet" description="Create an activity to link cinema and other event types to showtimes." />
            : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Cinema activities"><table className="w-full min-w-[38rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Name</th><th scope="col" className="px-4 py-3">Kind</th><th scope="col" className="px-4 py-3">Status</th><th scope="col" className="px-4 py-3">Activity ID</th></tr></thead><tbody>{rows.map((activity) => <tr key={activity.id} className="border-t"><td className="px-4 py-3"><Link className="font-medium underline-offset-4 hover:underline" to={`/cinema/activities/${encodeURIComponent(activity.id)}`}>{activity.name}</Link></td><td className="px-4 py-3">{activity.kind}</td><td className="px-4 py-3">{activity.status}</td><td className="px-4 py-3 font-mono text-xs">{activity.id}</td></tr>)}</tbody></table></div>}
      <PageControls hasMore={hasMore} loading={page.isFetching} onMore={() => page.currentData?.pagination.cursor && setCursor(page.currentData.pagination.cursor)} />
    </section>;
  }

  if (!createMode && (detail.isLoading || !detail.currentData) && !detail.isError) return <LoadingRows label="Loading activity" columns={2} />;
  if (!createMode && (detail.isError || !detail.currentData)) return <CinemaLoadError label="activity" retry={() => void detail.refetch()} />;
  const activity = detail.currentData;

  return <section className="space-y-5">
    <Button asChild variant="ghost"><Link to="/cinema/activities">← Activities</Link></Button>
    <header><h1 className="text-2xl font-bold">{createMode ? 'Create activity' : activity?.name ?? 'Activity'}</h1><p className="text-sm text-muted-foreground">Activity ID: {activity?.id ?? 'Assigned on save'}</p></header>
    {!editing && activity && <>
      <dl className="grid gap-4 border-y py-4 sm:grid-cols-2"><Detail label="Kind" value={activity.kind} /><Detail label="Status" value={activity.status} /><Detail label="Organization ID" value={activity.orgId} /></dl>
      <div className="flex flex-wrap gap-2"><Button type="button" variant="outline" onClick={() => setEditing(true)}>Edit activity</Button><Button type="button" variant="destructive" onClick={() => setConfirmDelete(true)}>Delete activity</Button></div>
    </>}
    {editing && !createMode && adminDetail.isLoading && <p role="status">Loading activity configuration…</p>}
    {editing && adminDetail.isError && <CinemaLoadError label="editable activity" retry={() => void adminDetail.refetch()} />}
    {editing && (createMode || adminDetail.currentData) && <form className="max-w-2xl space-y-4" onSubmit={(event) => void save(event)}>
      <Field label="Activity name" id="activity-name"><Input id="activity-name" required maxLength={200} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
      <Field label="Kind" id="activity-kind"><select id="activity-kind" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value as Activity['kind'] })}>{kinds.map((kind) => <option key={kind} value={kind}>{kind}</option>)}</select></Field>
      <Field label="Status" id="activity-status"><Input id="activity-status" required maxLength={40} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} /></Field>
      <Field label="Configuration (JSON object)" id="activity-config"><textarea id="activity-config" className="min-h-32 w-full rounded-md border border-input bg-background p-3 font-mono text-sm" value={form.config} onChange={(event) => setForm({ ...form, config: event.target.value })} aria-describedby="activity-config-help" /><span id="activity-config-help" className="text-xs text-muted-foreground">Only fields accepted by the service are stored. No business rules are inferred here.</span></Field>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-2"><Button type="button" variant="outline" disabled={createState.isLoading || updateState.isLoading} onClick={() => createMode ? navigate('/cinema/activities') : setEditing(false)}>Cancel</Button><Button type="submit" disabled={!form.name.trim() || createState.isLoading || updateState.isLoading}>{createState.isLoading || updateState.isLoading ? 'Saving…' : 'Save activity'}</Button></div>
    </form>}
    {confirmDelete && <div role="group" aria-label="Confirm activity deletion" className="space-y-3 rounded-md border border-destructive/40 p-4"><p>Delete activity <strong>{activity?.name}</strong>? The service may reject deletion while the activity is referenced.</p><div className="flex gap-2"><Button type="button" variant="destructive" disabled={deleteState.isLoading} onClick={() => void remove()}>{deleteState.isLoading ? 'Deleting…' : 'Confirm delete'}</Button><Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button></div></div>}
    {error && !editing && <p role="alert" className="text-sm text-destructive">{error}</p>}
  </section>;
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs font-medium text-muted-foreground">{label}</dt><dd className="mt-1 break-all text-sm">{value || '—'}</dd></div>;
}
